import moment from 'moment';
import { formatLongDateTime } from '../../../utils/dateFormat';
import { toast as message } from 'sonner';
import RelatedLookupCell from '../../RelatedLookupCell';
import { ClientSideRelationshipCellRenderer, ServerSideRelationshipCellRenderer } from '../renderers/RelationshipCellRenderer';
import { FileCellRenderer } from '../renderers/FileCellRenderer';
import { CopyableCellRenderer } from '../renderers/CopyableCellRenderer';
import { trimEmail, isValidEmail, validateAndNormalizePhone } from '../../../utils/search';
import { tableGridSourceName, type GridFilterOption, type GridFilterOptionRequest } from '../../../utils/gridFilterOptions';
import { checkboxFilter, typedFilterWithCheckboxes } from '../gridCheckboxFilters';
import type {
  ExtendedColDef,
  CurrencyFormatter,
  NumberFormatter,
  PicklistConfig,
  ClientSideSelectTables,
  CRMCellValueChangedEvent
} from '../../../types/grid';
import type { EnrichedTableField } from '../../../types/metadata';
import type { GridConfiguration } from '../../../types/grid';

// Header-based minimum width — a column is never allowed to be narrower than
// its header text. Shared with the runtime clamp in TableGrid.handleFirstDataRendered
// so the rule also applies to metadata-sized (flex/width) columns, which skip
// that runtime pass. AG Grid flex respects minWidth (scrolls if totals exceed).
const HEADER_FONT = 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
// The header's side padding plus its filter and menu buttons take about 72px;
// less than that cuts short headers to "Ope...".
const HEADER_PADDING = 76;
let _headerCanvas: HTMLCanvasElement | null = null;
export function headerMinWidth(headerName: string): number {
  if (!headerName || typeof document === 'undefined') return HEADER_PADDING;
  if (!_headerCanvas) _headerCanvas = document.createElement('canvas');
  const ctx = _headerCanvas.getContext('2d');
  if (!ctx) return HEADER_PADDING;
  ctx.font = HEADER_FONT;
  return Math.ceil(ctx.measureText(headerName).width) + HEADER_PADDING;
}

/**
 * agRichSelectCellEditor returns false from isPopup(), so AG Grid lays its
 * option list out inside the cell. Rows here are ~34px, so an open list would
 * paint over every row beneath it. Rendering it in a popup layer keeps the rows
 * visible behind the list.
 */
const RICH_SELECT_POPUP = {
  cellEditorPopup: true,
  cellEditorPopupPosition: 'under',
} as const;

/**
 * Parameters required for generating column configurations
 */
export interface ColumnConfigParams {
  isEditable: boolean;
  /** Per-field write permission for the current user (useFieldEditAccess). */
  canEditField?: (fieldName: string) => boolean;
  /**
   * Cells open their editor on a single click, so the whole cell must be a
   * click target and record links inside editable cells have to stand down.
   * Mirrors TableGrid's own singleClickEdit condition.
   */
  clickToEdit: boolean;
  showFilters: boolean;
  showMenu: boolean;
  clientSideTables: string[];
  metadata: {
    clientSideSelectTables?: ClientSideSelectTables;
    tableData?: {
      byName?: Record<string, { ui_label: string }>;
    };
  };
  schemaName: string;
  tableName: string;
  source?: 'view' | 'deepView' | 'base';
  tablePicklists: Record<string, PicklistConfig>;
  formatNumber: NumberFormatter;
  formatCurrency: CurrencyFormatter;
  isClientSideRow: (rowData: any) => boolean;
  onCellValueChanged: (event: CRMCellValueChangedEvent) => void;
}

const BLANK_OPTION: GridFilterOption = { value: null, label: '(Blanks)' };

/**
 * The view column a field is read from. Calculated fields are exposed as
 * `<name>_calc` by the generated views (the base table's same-named column,
 * where one exists, is not the calculated value), so the grid reads, filters
 * and sorts on `<name>_calc`. Expanded relationship columns (`*_ref`) keep
 * their own name.
 */
export function gridColumnName(tableField: EnrichedTableField): string {
  const name = tableField.name || '';
  const relatedField = (tableField.ui_data as { source?: string } | null | undefined)?.source === 'related_field';
  return tableField.is_calculated && name && !name.endsWith('_ref') && !relatedField ? `${name}_calc` : name;
}

/**
 * REG-832: every value merchant.deal_status_calc can take (merchant_deal_status_v):
 * the submission-derived statuses plus the merchant's own status when its
 * current cycle has no submission (the status picklist). A fixed list, because
 * listing distinct values would compute the status for every merchant.
 */
const MERCHANT_DEAL_STATUSES = ['Funded', 'Contracts In', 'Contracts Out', 'Approved', 'Missing documents',
  'Waiting On', 'Submitted', 'No Response', 'Declined'];

function remoteOptionRequest(
  tableField: EnrichedTableField,
  params: ColumnConfigParams,
  labelColumnName?: string,
): GridFilterOptionRequest {
  return {
    sourceSchema: params.schemaName,
    sourceName: tableGridSourceName(params.tableName, params.source),
    columnName: gridColumnName(tableField),
    labelColumnName,
  };
}

/**
 * Generates column configuration for relationship fields (client-side)
 */
export function generateClientSideRelationshipConfig(
  tableField: EnrichedTableField,
  baseConfig: ExtendedColDef,
  params: ColumnConfigParams,
  fieldFilterable: boolean
): ExtendedColDef {
  const { metadata } = params;
  const relationship = tableField.relationship!;

  // Assertive null check: relationship MUST have valid target_table_name
  if (!relationship.target_table_name) {
    throw new Error(`Invalid relationship for field ${tableField.name}: missing target_table_name`);
  }

  // Build sorted list of {id, label} for this relationship's target table
  const items = (metadata?.clientSideSelectTables?.[relationship.target_table_name!] || [])
    .slice()
    .sort((a: any, b: any) => String(a.label).localeCompare(String(b.label)));

  return {
    ...baseConfig,
    context: { ...baseConfig.context, export: { link: { table: relationship.target_table_name } } },
    cellRenderer: ClientSideRelationshipCellRenderer,
    cellRendererParams: {
      relationship: relationship,
      clientSideSelectTables: metadata?.clientSideSelectTables || {},
      suppressLink: params.clickToEdit && baseConfig.context?.editable === true
    },
    cellEditor: 'agRichSelectCellEditor',
    ...RICH_SELECT_POPUP,
    keyCreator: (cellParams: any) => String(cellParams.value),
    cellEditorParams: {
      values: [...items.map((item: any) => item.id), null],
      formatValue: (value: any) => {
        if (value === null) return 'Clear';
        const key = `${relationship.target_table_name}_${value}`;
        return metadata?.clientSideSelectTables?.flattened?.[key]?.label || String(value);
      },
      valueListMaxHeight: 300,
      valueListMaxWidth: 250,
      searchType: 'matchAny',
      highlightMatch: true,
    },
    valueFormatter: (cellParams: any) => {
      if (cellParams.value === null) {
        return '';
      }
      const key = `${relationship.target_table_name}_${cellParams.value}`;
      return metadata?.clientSideSelectTables?.flattened?.[key]?.label || '';
    },
    ...(fieldFilterable
      ? checkboxFilter({ staticOptions: [
          ...items.map((item: any) => ({ value: item.id, label: String(item.label ?? item.id) })),
          BLANK_OPTION,
        ] })
      : { filter: false })
  };
}

/**
 * Generates column configuration for relationship fields (server-side)
 */
export function generateServerSideRelationshipConfig(
  tableField: EnrichedTableField,
  baseConfig: ExtendedColDef,
  params: ColumnConfigParams,
  fieldFilterable: boolean
): ExtendedColDef {
  const { metadata, schemaName, onCellValueChanged } = params;
  const relationship = tableField.relationship!;
  
  // Assertive null check: relationship MUST have valid target_table_name
  if (!relationship.target_table_name) {
    throw new Error(`Invalid relationship for field ${tableField.name}: missing target_table_name`);
  }

  return {
    ...baseConfig,
    context: { ...baseConfig.context, export: { fields: [`${tableField.name}_rel`], link: { table: relationship.target_table_name } } },
    cellRenderer: ServerSideRelationshipCellRenderer,
    cellRendererParams: {
      fieldName: tableField.name,
      relationship: relationship,
      suppressLink: params.clickToEdit && baseConfig.context?.editable === true
    },
    ...(fieldFilterable
      ? typedFilterWithCheckboxes(
          'agTextColumnFilter',
          {
            filterPlaceholder: `Search for a ${metadata?.tableData?.byName?.[relationship.target_table_name!]?.ui_label}`,
            filterOptions: ['contains'],
            maxNumConditions: 1,
            buttons: ['clear'],
          },
          { optionRequest: remoteOptionRequest(tableField, params, `${tableField.name}_rel`) },
        )
      : { filter: false }),
    cellEditor: RelatedLookupCell as any,
    keyCreator: (cellParams: any) => String(cellParams.value),
    cellEditorParams: (cellParams: any) => {
      return {
        value: cellParams.value,
        relationship: relationship,
        schema: schemaName,
        onCellValueChanged: onCellValueChanged,
        currentDisplayValue: cellParams?.data[`${tableField.name}_rel`]?.label || null,
      };
    },
    valueFormatter: (cellParams: any) => {
      if (cellParams.value === null) {
        return '';
      }
      let displayValue = 'Error';
      if (cellParams?.value?.data?.label) {
        displayValue = cellParams.value.data.label;
      } else if (cellParams?.data?.[`${tableField.name}_rel`]?.label) {
        displayValue = cellParams.data[`${tableField.name}_rel`].label;
      }
      return displayValue;
    }
  };
}

/**
 * Generates column configuration for picklist fields
 */
export function generatePicklistConfig(
  _tableField: EnrichedTableField,
  baseConfig: ExtendedColDef,
  picklistConfig: PicklistConfig,
  fieldFilterable: boolean,
  _params: ColumnConfigParams
): ExtendedColDef {
  return {
    ...baseConfig,
    ...(fieldFilterable
      ? checkboxFilter({ staticOptions: [
          ...(picklistConfig.values ?? []).map((item: any) => ({
            value: item.value,
            label: String(item.label ?? item.value),
          })),
          BLANK_OPTION,
        ] })
      : { filter: false }),
    cellEditor: 'agRichSelectCellEditor',
    ...RICH_SELECT_POPUP,
    cellEditorParams: {
      values: picklistConfig.values
        ? [...picklistConfig.values.filter((item: any) => !!item?.value).map((item: any) => item.value), null]
        : [],
      formatValue: (value: any) => (value === null ? 'Clear' : value),
      valueListMaxHeight: 300,
      valueListMaxWidth: 200,
      searchType: 'matchAny',
      highlightMatch: true,
    },
  };
}

/**
 * Generates column configuration for date/timestamp fields
 */
export function generateDateConfig(
  tableField: EnrichedTableField,
  baseConfig: ExtendedColDef,
  fieldFilterable: boolean,
  params: ColumnConfigParams,
): ExtendedColDef {
  return {
    width: 120,
    ...baseConfig,
    ...(fieldFilterable
      ? typedFilterWithCheckboxes(
          'agDateColumnFilter',
          { buttons: ['clear'] },
          { optionRequest: remoteOptionRequest(tableField, params) },
        )
      : { filter: false }),
    cellEditor: 'agDateCellEditor',
    context: { ...baseConfig.context, export: { excel: { kind: tableField.data_type === 'date' ? 'date' : 'timestamp_date', dateFormat: 'mm/dd/yyyy' } } },
    valueFormatter: (cellParams: any) => {
      return cellParams.value ? moment(cellParams.value).format('MM/DD/YYYY') : '';
    }
  };
}

/**
 * Generates column configuration for numeric fields
 */
export function generateNumericConfig(
  tableField: EnrichedTableField,
  baseConfig: ExtendedColDef,
  params: ColumnConfigParams,
  fieldFilterable: boolean
): ExtendedColDef {
  const { formatNumber, formatCurrency } = params;
  const isWholeNumberType = ['integer', 'bigint', 'smallint'].includes(tableField.data_type ?? '');

  const numericConfig: ExtendedColDef = {
    width: tableField.ui_is_currency ? 130 : 110,
    ...baseConfig,
    ...(fieldFilterable
      ? typedFilterWithCheckboxes(
          'agNumberColumnFilter',
          { buttons: ['clear'] },
          { optionRequest: remoteOptionRequest(tableField, params) },
        )
      : { filter: false }),
    cellEditor: 'agNumberCellEditor',
    ...(tableField.name === 'id' ? {} : {
      context: { ...baseConfig.context, export: { excel: { kind: 'number', precision: isWholeNumberType ? 0 : tableField.ui_scale || 0 } } },
    }),
    valueFormatter: (cellParams: any) => {
      // A record id reads as the plain number ("20274"), never "20,274.000000000";
      // whole-number columns never show decimals even when the field metadata
      // carries a scale (most integer fields do, as 9).
      if (tableField.name === 'id') return cellParams.value == null ? '' : String(cellParams.value);
      return formatNumber(cellParams.value, isWholeNumberType ? 0 : tableField.ui_scale || undefined);
    },
    valueParser: (cellParams: any) => {
      const parsedValue = parseFloat(cellParams.newValue);
      return isNaN(parsedValue) ? null : parsedValue;
    }
  };

  if (tableField.ui_is_currency) {
    return {
      ...numericConfig,
      context: { ...baseConfig.context, export: { excel: { kind: 'currency', precision: tableField.ui_scale || 0 } } },
      valueFormatter: (cellParams: any) => {
        return formatCurrency(cellParams.value, tableField.ui_scale || undefined);
      },
      valueParser: (cellParams: any) => {
        const parsedValue = parseFloat(cellParams.newValue);
        return isNaN(parsedValue) ? null : parsedValue;
      }
    };
  }
  return numericConfig;
}

/**
 * Generates column configuration for boolean fields
 */
export function generateBooleanConfig(
  _tableField: EnrichedTableField,
  baseConfig: ExtendedColDef,
  fieldFilterable: boolean
): ExtendedColDef {
  return {
    width: 90,
    ...baseConfig,
    ...(fieldFilterable
      ? checkboxFilter({ staticOptions: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
          BLANK_OPTION,
        ] })
      : { filter: false }),
    cellEditor: 'agCheckboxCellEditor',
    cellRenderer: 'agCheckboxCellRenderer',
  };
}

/**
 * Generates column configuration for text fields
 */
export function generateTextConfig(
  tableField: EnrichedTableField,
  baseConfig: ExtendedColDef,
  fieldFilterable: boolean,
  params: ColumnConfigParams,
): ExtendedColDef {
  return {
    ...baseConfig,
    ...(fieldFilterable
      ? typedFilterWithCheckboxes(
          'agTextColumnFilter',
          { filterOptions: ['contains'], maxNumConditions: 1, buttons: ['clear'] },
          { optionRequest: remoteOptionRequest(tableField, params) },
        )
      : { filter: false })
  };
}

/**
 * Generates column configuration for email fields with validation
 */
export function generateEmailConfig(
  tableField: EnrichedTableField,
  baseConfig: ExtendedColDef,
  fieldFilterable: boolean,
  params: ColumnConfigParams,
): ExtendedColDef {
  return {
    ...baseConfig,
    cellRenderer: CopyableCellRenderer,
    ...(fieldFilterable
      ? typedFilterWithCheckboxes(
          'agTextColumnFilter',
          { filterOptions: ['contains'], maxNumConditions: 1, buttons: ['clear'] },
          { optionRequest: remoteOptionRequest(tableField, params) },
        )
      : { filter: false }),
    valueParser: (params: any) => {
      const trimmed = trimEmail(params.newValue);

      if (trimmed && !isValidEmail(trimmed)) {
        message.error(`Invalid email format: ${params.newValue}`);
        setTimeout(() => {
          params.api.undoCellEditing();
        }, 0);
        return params.oldValue;
      }

      return trimmed;
    }
  };
}

/**
 * Generates column configuration for phone fields with validation
 */
export function generatePhoneConfig(
  tableField: EnrichedTableField,
  baseConfig: ExtendedColDef,
  fieldFilterable: boolean,
  params: ColumnConfigParams,
): ExtendedColDef {
  return {
    width: 145,
    ...baseConfig,
    cellRenderer: CopyableCellRenderer,
    ...(fieldFilterable
      ? typedFilterWithCheckboxes(
          'agTextColumnFilter',
          { filterOptions: ['contains'], maxNumConditions: 1, buttons: ['clear'] },
          { optionRequest: remoteOptionRequest(tableField, params) },
        )
      : { filter: false }),
    valueParser: (params: any) => {
      if (!params.newValue || params.newValue.trim() === '') {
        return null;
      }

      // Validate but don't normalize - store as entered
      const result = validateAndNormalizePhone(params.newValue);

      if (!result.valid) {
        message.error(result.error);
        setTimeout(() => {
          params.api.undoCellEditing();
        }, 0);
        return params.oldValue;
      }

      // Return the original input (not normalized)
      return params.newValue;
    }
  };
}

/**
 * Generates column configuration for file fields
 */
export function generateFileConfig(
  _tableField: EnrichedTableField,
  baseConfig: ExtendedColDef
): ExtendedColDef {
  return {
    ...baseConfig,
    editable: false,
    cellRenderer: FileCellRenderer,
    filter: false,
    sortable: false,
    valueFormatter: (params: any) => {
      const files = Array.isArray(params.value) ? params.value : [];
      if (files.length === 0) return '';
      if (files.length === 1) return files[0].filename;
      return `${files.length} files`;
    },
  };
}

/**
 * Main function to generate column configuration from field metadata
 */
export function getColumnConfigFromField(
  tableField: EnrichedTableField,
  uiFieldConfig: GridConfiguration['ui_data']['fields'][0] | undefined,
  params: ColumnConfigParams
): ExtendedColDef {
  // Field editability logic
  let fieldEditable = !tableField.is_system && !tableField.is_calculated && params.isEditable !== false
    && (params.canEditField?.(tableField.name || '') ?? true);
  if (uiFieldConfig && uiFieldConfig.editable !== undefined) {
    fieldEditable = fieldEditable && uiFieldConfig.editable;
  }

  // Field filterability logic
  let fieldFilterable = params.showFilters !== false;
  if (uiFieldConfig && uiFieldConfig.filterable !== undefined) {
    fieldFilterable = fieldFilterable && uiFieldConfig.filterable;
  }

  const columnHeaderName = uiFieldConfig?.display_name || tableField.ui_label || tableField.name || '';

  const baseConfig: ExtendedColDef = {
    headerName: columnHeaderName,
    field: gridColumnName(tableField),
    suppressHeaderMenuButton: params.showMenu === false,
    context: { editable: fieldEditable },
    // Manual overrides from page builder — applied here so type-defaults below can't overwrite them
    ...(uiFieldConfig?.width !== undefined && { width: uiFieldConfig.width }),
    // flex sizes columns proportionally (by ratio) so they fill the grid and
    // stay consistent across screen widths
    ...(uiFieldConfig?.flex !== undefined && { flex: uiFieldConfig.flex }),
    // Enforce the header-width floor even when a width/flex is configured
    minWidth: Math.max(uiFieldConfig?.minWidth ?? 0, headerMinWidth(columnHeaderName)),
    ...(uiFieldConfig?.maxWidth !== undefined && { maxWidth: uiFieldConfig.maxWidth }),
    cellClass: (cellParams: any) => {
      const classes: string[] = [];
      if (params.isClientSideRow(cellParams.data)) {
        classes.push('client-side-row');
        if (tableField.is_system) {
          classes.push('disabled-ag-cell');
        } else if (tableField.is_required && !cellParams.value) {
          classes.push('required-ag-cell');
        }
      }
      return classes;
    },
  };

  // app_user stores inactivity as a suspension timestamp. Present the inverse
  // as a clear Active status while retaining suspended_at as the server-sort
  // field, so the Admin Users grid can sort without loading every user.
  if (tableField.table_name === 'app_user' && tableField.name === 'suspended_at') {
    return {
      ...baseConfig,
      headerName: 'Active',
      editable: false,
      sortable: true,
      filter: false,
      minWidth: Math.max(baseConfig.minWidth ?? 0, 95),
      valueFormatter: (cellParams: any) => cellParams.value ? 'No' : 'Yes',
      cellClass: (cellParams: any) => `user-active-cell ${cellParams.value ? 'inactive' : 'active'}`,
      headerTooltip: 'Yes means the user can sign in; No means the user is suspended',
    };
  }

  // Handle relationship fields
  if (tableField.relationship) {
    const isClientSide = params.clientSideTables.includes(tableField.relationship.target_table_name || '');
    
    if (isClientSide) {
      return generateClientSideRelationshipConfig(tableField, baseConfig, params, fieldFilterable);
    } else {
      return generateServerSideRelationshipConfig(tableField, baseConfig, params, fieldFilterable);
    }
  }

  // REG-832: the merchant status everyone sees (never moves backward). Read-only,
  // filtered from a fixed list of its possible values.
  if (tableField.table_name === 'merchant' && tableField.name === 'deal_status' && tableField.is_calculated) {
    const own = (params.tablePicklists.status?.values ?? []).map((item: any) => String(item.value));
    const values = [...new Set([...MERCHANT_DEAL_STATUSES, ...own])];
    return {
      ...baseConfig,
      editable: false,
      ...(fieldFilterable
        ? checkboxFilter({ staticOptions: [...values.map((value) => ({ value, label: value })), BLANK_OPTION] })
        : { filter: false }),
    };
  }

  // Handle picklist fields
  const picklistConfig = tableField.name ? params.tablePicklists[tableField.name] : null;
  if (picklistConfig) {
    return generatePicklistConfig(tableField, baseConfig, picklistConfig, fieldFilterable, params);
  }

  // Handle file fields
  if ((tableField as any).is_file) {
    return generateFileConfig(tableField, baseConfig);
  }

  // Handle email fields
  if (tableField.is_email) {
    return generateEmailConfig(tableField, baseConfig, fieldFilterable, params);
  }

  // Handle phone fields
  if (tableField.is_phone) {
    return generatePhoneConfig(tableField, baseConfig, fieldFilterable, params);
  }

  // submission.needs_reconciliation renders as a ⚠️ badge, not a checkbox: it
  // flags "funder reply awaiting human confirmation" (email ingestion queue).
  if (tableField.name === 'needs_reconciliation') {
    return {
      ...baseConfig,
      editable: false,
      width: 70,
      cellRenderer: (p: any) => (p.value ? '⚠️' : ''),
      cellStyle: { textAlign: 'center' },
      headerTooltip: 'Funder reply awaiting confirmation in Email Review',
      ...(fieldFilterable
        ? checkboxFilter({ staticOptions: [
            { value: true, label: 'Yes' },
            { value: false, label: 'No' },
            BLANK_OPTION,
          ] })
        : { filter: false }),
    };
  }

  // Handle different data types
  switch (tableField.data_type) {
    case 'date':
    case 'timestamp without time zone':
    case 'timestamp with time zone': {
      const dateConfig = generateDateConfig(tableField, baseConfig, fieldFilterable, params);
      if (uiFieldConfig?.date_format !== 'long') return dateConfig;
      // REG-828: the long format needs room for "Thursday, September 10, 2026 at 9:11:45 PM".
      return {
        ...dateConfig,
        context: { ...dateConfig.context, export: undefined },
        ...(uiFieldConfig.width === undefined && uiFieldConfig.flex === undefined ? { width: 330 } : {}),
        valueFormatter: (cellParams: any) => formatLongDateTime(cellParams.value),
      };
    }

    case 'numeric':
    case 'integer':
    case 'bigint':
      return generateNumericConfig(tableField, baseConfig, params, fieldFilterable);

    case 'boolean':
      return generateBooleanConfig(tableField, baseConfig, fieldFilterable);

    case 'text':
    case 'character varying':
    default:
      return generateTextConfig(tableField, baseConfig, fieldFilterable, params);
  }
}
